import React, { useState, useRef, useEffect } from 'react';
import { Modal, Form, Input, Button, App } from 'antd';
import { UserOutlined, LockOutlined, MobileOutlined, LeftOutlined } from '@ant-design/icons';
import { phonePasswordLogin, checkPhoneRegistered, register, sendSmsCode, resetPassword } from '../../api';
import { useNavigate } from 'react-router-dom';
import { setAuthSession } from '../../utils/auth';
import styles from './index.module.css';

const PHONE_RULES = [
    { required: true, message: '请输入手机号' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式' },
];

const LoginModal = ({ open, onCancel, onSuccess, continueAction }) => {
    const { modal, message } = App.useApp();
    const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot'

    const [loading, setLoading] = useState(false);
    const [registerLoading, setRegisterLoading] = useState(false);
    const [forgotLoading, setForgotLoading] = useState(false);

    const [phoneHint, setPhoneHint] = useState('');

    const [registerCodeSending, setRegisterCodeSending] = useState(false);
    const [registerCodeCountdown, setRegisterCodeCountdown] = useState(0);
    const [resetCodeSending, setResetCodeSending] = useState(false);
    const [resetCodeCountdown, setResetCodeCountdown] = useState(0);

    const [form] = Form.useForm();
    const [registerForm] = Form.useForm();
    const [forgotForm] = Form.useForm();
    const navigate = useNavigate();

    const registerTimerRef = useRef(null);
    const resetTimerRef = useRef(null);

    useEffect(() => {
        const registerTimer = registerTimerRef.current;
        const resetTimer = resetTimerRef.current;
        return () => {
            if (registerTimer) clearInterval(registerTimer);
            if (resetTimer) clearInterval(resetTimer);
        };
    }, []);

    // 每次弹窗打开时回到登录界面
    useEffect(() => {
        if (open) {
            setMode('login');
            setPhoneHint('');
        }
    }, [open]);

    const switchMode = (nextMode) => {
        setMode(nextMode);
        setPhoneHint('');
    };

    const closeAndFinish = () => {
        message.success('登录成功');
        onSuccess?.();
        onCancel?.();
        if (continueAction) {
            continueAction();
        } else {
            navigate('/wordcloud');
        }
    };

    const applyLoginSession = (data, fallbackPhone) => {
        const { token, user, is_admin } = data;
        setAuthSession({ token, username: user?.name || user?.phone || fallbackPhone, isAdmin: is_admin, userId: user?.id, phone: user?.phone || fallbackPhone });
    };

    const handleLoginSubmit = (values) => {
        setLoading(true);
        phonePasswordLogin({ phone: values.phone, password: values.password })
            .then(response => {
                if (response.data && response.data.err_code === 200 && response.data.data && response.data.data.token) {
                    applyLoginSession(response.data.data, values.phone);
                    closeAndFinish();
                } else {
                    throw new Error(response.data?.err_msg || '账号或密码错误');
                }
            })
            .catch(err => {
                const errorText = err?.response?.data?.err_msg || err?.message || '登录失败，请重试'
                modal.error({
                    title: '登录失败',
                    content: errorText,
                    centered: true,
                    okText: '知道了',
                })
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleRegisterPhoneBlur = async (e) => {
        const phone = e.target.value;
        setPhoneHint('');
        if (!phone || !/^1[3-9]\d{9}$/.test(phone)) return;

        try {
            const response = await checkPhoneRegistered({ phone });
            if (response.data && response.data.err_code === 200) {
                const { registered } = response.data.data;
                if (registered) {
                    setPhoneHint('该手机号已被注册');
                }
            }
        } catch (err) {
            console.error('检查手机号失败', err);
        }
    };

    const startCountdown = (setCountdown, timerRef) => {
        setCountdown(60);
        timerRef.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleSendRegisterCode = async () => {
        const phone = registerForm.getFieldValue('phone');
        if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
            registerForm.validateFields(['phone']).catch(() => {});
            message.warning('请输入正确的手机号');
            return;
        }
        setRegisterCodeSending(true);
        try {
            const response = await sendSmsCode({ phone });
            if (response.data && response.data.err_code === 200) {
                message.success('验证码已发送，请注意查收');
                startCountdown(setRegisterCodeCountdown, registerTimerRef);
            } else {
                throw new Error(response.data?.err_msg || '验证码发送失败');
            }
        } catch (err) {
            message.error(err.message || '验证码发送失败，请重试');
        } finally {
            setRegisterCodeSending(false);
        }
    };

    const handleSendResetCode = async () => {
        const phone = forgotForm.getFieldValue('phone');
        if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
            forgotForm.validateFields(['phone']).catch(() => {});
            message.warning('请输入正确的手机号');
            return;
        }
        setResetCodeSending(true);
        try {
            const response = await sendSmsCode({ phone, target: 'update_pwd' });
            if (response.data && response.data.err_code === 200) {
                message.success('验证码已发送，请注意查收');
                startCountdown(setResetCodeCountdown, resetTimerRef);
            } else {
                throw new Error(response.data?.err_msg || '验证码发送失败');
            }
        } catch (err) {
            message.error(err.message || '验证码发送失败，请重试');
        } finally {
            setResetCodeSending(false);
        }
    };

    const handleRegisterSubmit = (values) => {
        setRegisterLoading(true);
        register({ phone: values.phone, password: values.password, phone_code: values.code })
            .then(response => {
                if (response.data && response.data.err_code === 200) {
                    return phonePasswordLogin({ phone: values.phone, password: values.password });
                } else {
                    throw new Error(response.data.err_msg || '注册失败');
                }
            })
            .then(response => {
                if (response.data && response.data.err_code === 200 && response.data.data && response.data.data.token) {
                    applyLoginSession(response.data.data, values.phone);
                    closeAndFinish();
                } else {
                    throw new Error(response.data.err_msg || '自动登录失败');
                }
            })
            .catch(err => {
                message.error(err.message || '注册失败，请重试');
            })
            .finally(() => {
                setRegisterLoading(false);
            });
    };

    const handleForgotSubmit = (values) => {
        setForgotLoading(true);
        resetPassword({ phone: values.phone, phone_code: values.code, password: values.password })
            .then(response => {
                if (response.data && response.data.err_code === 200) {
                    message.success('密码重置成功，请使用新密码登录');
                    forgotForm.resetFields();
                    form.setFieldsValue({ phone: values.phone, password: undefined });
                    switchMode('login');
                } else {
                    throw new Error(response.data?.err_msg || '密码重置失败');
                }
            })
            .catch(err => {
                message.error(err.message || '密码重置失败，请重试');
            })
            .finally(() => {
                setForgotLoading(false);
            });
    };

    const renderBackLink = () => (
        <div className={styles.backLink} onClick={() => switchMode('login')}>
            <LeftOutlined /> 返回登录
        </div>
    );

    const titleMap = {
        login: continueAction ? '请先登录' : '欢迎登录',
        register: '手机号注册',
        forgot: '找回密码',
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            centered
            width={400}
            className={styles.loginModal}
        >
            <div className={styles.modalContent}>
                {mode !== 'login' && renderBackLink()}
                <h2 className={styles.modalTitle}>{titleMap[mode]}</h2>

                {mode === 'login' && (
                    <Form
                        form={form}
                        className={styles.loginForm}
                        onFinish={handleLoginSubmit}
                        layout="vertical"
                    >
                        <Form.Item
                            name="phone"
                            rules={[{ required: true, message: '请输入账号' }]}
                        >
                            <Input
                                size="large"
                                prefix={<UserOutlined className={styles.inputIcon} />}
                                placeholder="请输入账号"
                            />
                        </Form.Item>
                        <Form.Item
                            name="password"
                            rules={[{ required: true, message: '请输入密码' }]}
                        >
                            <Input.Password
                                size="large"
                                prefix={<LockOutlined className={styles.inputIcon} />}
                                placeholder="请输入密码"
                            />
                        </Form.Item>

                        <div className={styles.authLinksRow}>
                            <span className={styles.authLink} onClick={() => switchMode('register')}>立即注册</span>
                            <span className={styles.authLinkDivider}>|</span>
                            <span className={styles.authLink} onClick={() => switchMode('forgot')}>找回密码</span>
                        </div>

                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                loading={loading}
                                block
                                className={styles.loginBtn}
                            >
                                {continueAction ? '登录并继续' : '登录'}
                            </Button>
                        </Form.Item>
                    </Form>
                )}

                {mode === 'register' && (
                    <Form
                        form={registerForm}
                        className={styles.loginForm}
                        onFinish={handleRegisterSubmit}
                        layout="vertical"
                    >
                        <Form.Item
                            name="phone"
                            rules={PHONE_RULES}
                            validateStatus={phoneHint ? 'warning' : ''}
                            help={phoneHint || undefined}
                        >
                            <Input
                                size="large"
                                prefix={<MobileOutlined className={styles.inputIcon} />}
                                placeholder="请输入手机号"
                                onBlur={handleRegisterPhoneBlur}
                            />
                        </Form.Item>
                        <Form.Item
                            name="password"
                            rules={[
                                { required: true, message: '请输入密码' },
                                { min: 6, message: '密码至少6位' }
                            ]}
                        >
                            <Input.Password
                                size="large"
                                prefix={<LockOutlined className={styles.inputIcon} />}
                                placeholder="请输入密码（至少6位）"
                            />
                        </Form.Item>
                        <Form.Item
                            name="confirmPassword"
                            dependencies={['password']}
                            rules={[
                                { required: true, message: '请确认密码' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('两次输入的密码不一致'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                size="large"
                                prefix={<LockOutlined className={styles.inputIcon} />}
                                placeholder="请再次输入密码"
                            />
                        </Form.Item>
                        <div className={styles.codeRow}>
                            <Form.Item
                                name="code"
                                rules={[{ required: true, message: '请输入验证码' }]}
                                className={styles.codeFormItem}
                            >
                                <Input
                                    size="large"
                                    prefix={<MobileOutlined className={styles.inputIcon} />}
                                    placeholder="请输入验证码"
                                />
                            </Form.Item>
                            <Button
                                size="large"
                                className={styles.codeBtn}
                                disabled={registerCodeCountdown > 0}
                                loading={registerCodeSending}
                                onClick={handleSendRegisterCode}
                            >
                                {registerCodeCountdown > 0 ? `${registerCodeCountdown}秒后重新获取` : '获取验证码'}
                            </Button>
                        </div>
                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                loading={registerLoading}
                                block
                                className={styles.loginBtn}
                            >
                                {continueAction ? '注册并继续' : '注册'}
                            </Button>
                        </Form.Item>
                    </Form>
                )}

                {mode === 'forgot' && (
                    <Form
                        form={forgotForm}
                        className={styles.loginForm}
                        onFinish={handleForgotSubmit}
                        layout="vertical"
                    >
                        <Form.Item
                            name="phone"
                            rules={PHONE_RULES}
                        >
                            <Input
                                size="large"
                                prefix={<MobileOutlined className={styles.inputIcon} />}
                                placeholder="请输入手机号"
                            />
                        </Form.Item>
                        <div className={styles.codeRow}>
                            <Form.Item
                                name="code"
                                rules={[{ required: true, message: '请输入验证码' }]}
                                className={styles.codeFormItem}
                            >
                                <Input
                                    size="large"
                                    prefix={<MobileOutlined className={styles.inputIcon} />}
                                    placeholder="请输入验证码"
                                />
                            </Form.Item>
                            <Button
                                size="large"
                                className={styles.codeBtn}
                                disabled={resetCodeCountdown > 0}
                                loading={resetCodeSending}
                                onClick={handleSendResetCode}
                            >
                                {resetCodeCountdown > 0 ? `${resetCodeCountdown}秒后重新获取` : '获取验证码'}
                            </Button>
                        </div>
                        <Form.Item
                            name="password"
                            rules={[
                                { required: true, message: '请输入新密码' },
                                { min: 6, message: '密码至少6位' }
                            ]}
                        >
                            <Input.Password
                                size="large"
                                prefix={<LockOutlined className={styles.inputIcon} />}
                                placeholder="请输入新密码（至少6位）"
                            />
                        </Form.Item>
                        <Form.Item
                            name="confirmPassword"
                            dependencies={['password']}
                            rules={[
                                { required: true, message: '请确认新密码' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('两次输入的密码不一致'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                size="large"
                                prefix={<LockOutlined className={styles.inputIcon} />}
                                placeholder="请再次输入新密码"
                            />
                        </Form.Item>
                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                loading={forgotLoading}
                                block
                                className={styles.loginBtn}
                            >
                                提交
                            </Button>
                        </Form.Item>
                    </Form>
                )}
            </div>
        </Modal>
    );
};

export default LoginModal;